import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { priceSubmissionService, PriceSubmission } from '../services/api';
import { ChevronUp, ChevronDown } from 'lucide-react';

const PriceManagementPage = () => {
  const { t } = useTranslation();
  const [submissions, setSubmissions] = useState<PriceSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [editDate, setEditDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('submittedAt');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  useEffect(() => {
    fetchSubmissions();
  }, [currentPage, searchQuery, sortBy, sortOrder]);

  const fetchSubmissions = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {
        status: 'approved',
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder,
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await priceSubmissionService.getPriceSubmissions(params);
      setSubmissions(response.data.data.submissions || []);
      setTotalPages(Math.ceil((response.data.data.total || 0) / 20));
    } catch (error: any) {
      console.error('Failed to fetch submissions:', error);
      setError(t('priceManagement.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(key);
      setSortOrder('ASC'); // Default to ASC when switching columns, or DESC if preferred
    }
    setCurrentPage(1); // Reset to first page on sort change
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) return <div className="w-4 h-4 ml-1 inline-block opacity-20"><ChevronUp className="w-4 h-4" /></div>;
    return sortOrder === 'ASC' ? (
      <div className="w-4 h-4 ml-1 inline-block"><ChevronUp className="w-4 h-4" /></div>
    ) : (
      <div className="w-4 h-4 ml-1 inline-block"><ChevronDown className="w-4 h-4" /></div>
    );
  };

  const handleEdit = (submission: PriceSubmission) => {
    setEditingId(submission.id);
    setEditPrice(submission.submittedPrice.toString());
    setEditDate(submission.priceDate.split('T')[0]);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditPrice('');
    setEditDate('');
  };

  const handleSaveEdit = async (id: number) => {
    try {
      const price = parseFloat(editPrice);
      if (isNaN(price) || price <= 0) {
        alert(t('priceManagement.messages.invalidPrice'));
        return;
      }

      await priceSubmissionService.adminUpdatePriceSubmission(id, {
        submittedPrice: price,
        priceDate: editDate,
      });

      setEditingId(null);
      setEditPrice('');
      setEditDate('');
      fetchSubmissions();
      // Optional: Show success message
    } catch (error: any) {
      console.error('Failed to update submission:', error);
      alert(t('priceManagement.messages.updateFailed'));
    }
  };

  const handleDelete = async (id: number, cardName: string) => {
    if (!confirm(t('priceManagement.messages.confirmDelete', { cardName }))) {
      return;
    }

    try {
      await priceSubmissionService.adminDeletePriceSubmission(id);
      fetchSubmissions();
    } catch (error: any) {
      console.error('Failed to delete submission:', error);
      alert(t('priceManagement.messages.deleteFailed'));
    }
  };

  if (loading && submissions.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">{t('priceManagement.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && submissions.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchSubmissions}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            {t('leaderboard.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('priceManagement.title')}</h1>
          <p className="text-gray-600">{t('priceManagement.description')}</p>
        </div>

        {/* Search Filter */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label htmlFor="searchQuery" className="block text-sm font-medium text-gray-700 mb-2">
                {t('priceManagement.filters.cardId')}
              </label>
              <input
                type="text"
                id="searchQuery"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('priceManagement.searchPlaceholder')}
              />
            </div>
            <button
              onClick={() => {
                setSearchQuery('');
                setCurrentPage(1);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              {t('priceManagement.buttons.clear')}
            </button>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition select-none"
                    onClick={() => handleSort('card_name')}
                  >
                    <div className="flex items-center">
                      {t('priceManagement.titles.card')}
                      <SortIcon column="card_name" />
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition select-none"
                    onClick={() => handleSort('submittedPrice')}
                  >
                    <div className="flex items-center">
                      {t('priceManagement.titles.price')}
                      <SortIcon column="submittedPrice" />
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition select-none"
                    onClick={() => handleSort('priceDate')}
                  >
                    <div className="flex items-center">
                      {t('priceManagement.titles.priceDate')}
                      <SortIcon column="priceDate" />
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition select-none"
                    onClick={() => handleSort('source')}
                  >
                    <div className="flex items-center">
                      {t('priceManagement.titles.source')}
                      <SortIcon column="source" />
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition select-none"
                    onClick={() => handleSort('submitter_name')}
                  >
                    <div className="flex items-center">
                      {t('priceManagement.titles.submitter')}
                      <SortIcon column="submitter_name" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('priceManagement.titles.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {submissions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      {t('priceManagement.messages.noSubmissions')}
                    </td>
                  </tr>
                ) : (
                  submissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900 flex items-center gap-2">
                            {submission.card?.name || t('priceModeration.card.unknown')}
                            {submission.card?.language && (
                              <span className={`text-xs px-1.5 py-0.5 rounded border ${submission.card.language === 'EN'
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : 'bg-gray-50 text-gray-600 border-gray-200'
                                }`}>
                                {submission.card.language}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">{t('priceManagement.titles.card')} ID: {submission.cardId}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {editingId === submission.id ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded"
                          />
                        ) : (
                          <span className="font-semibold text-blue-600">
                            ฿{Number(submission.submittedPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingId === submission.id ? (
                          <input
                            type="date"
                            value={editDate}
                            onChange={(e) => setEditDate(e.target.value)}
                            className="w-36 px-2 py-1 border border-gray-300 rounded"
                          />
                        ) : (
                          <span className="text-sm text-gray-600">{new Date(submission.priceDate).toLocaleDateString()}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <p className="text-gray-900">{submission.source}</p>
                          {submission.sourceUrl && (
                            <a
                              href={submission.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline"
                            >
                              {t('priceManagement.buttons.viewSource')}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {submission.submitter?.profilePicture && (
                            <img
                              src={submission.submitter.profilePicture}
                              alt={submission.submitter.displayName}
                              className="w-6 h-6 rounded-full"
                            />
                          )}
                          <span className="text-sm text-gray-900">{submission.submitter?.displayName || t('priceModeration.card.unknownUser')}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {editingId === submission.id ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveEdit(submission.id)}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition"
                            >
                              {t('priceManagement.buttons.save')}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition"
                            >
                              {t('priceManagement.buttons.cancel')}
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(submission)}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition"
                            >
                              {t('priceManagement.buttons.edit')}
                            </button>
                            <button
                              onClick={() => handleDelete(submission.id, submission.card?.name || 'Unknown')}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition"
                            >
                              {t('priceManagement.buttons.delete')}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('browse.previous')}
            </button>
            <span className="px-4 py-2 bg-white border border-gray-300 rounded-lg">
              {t('userReviews.page', { page: currentPage, totalPages })}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('browse.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
export default PriceManagementPage;
